import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { notifyShareUpdate } from '../lib/notifyShareUpdate'

const useAuthStore = create((set, get) => ({
  user:             null,   // row from public.profiles
  session:          null,   // Supabase session object
  loading:          true,   // true until the initial session check resolves
  passwordRecovery: false,  // true when user arrives via password reset link
  pendingShares:    [],     // shared tasks waiting to be accepted

  /**
   * Call once on app mount.
   * Reads the current session, loads the profile, then subscribes to
   * future auth state changes (login / logout / token refresh).
   */
  init: async () => {
    // Register listener FIRST so we catch PASSWORD_RECOVERY from URL hash
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'INITIAL_SESSION') return
      if (event === 'PASSWORD_RECOVERY' && session) {
        const profile = await get()._fetchProfile(session.user.id)
        set({ session, user: profile, loading: false, passwordRecovery: true })
        return
      }
      if (event === 'TOKEN_REFRESHED') { set({ session }); return }
      if (event === 'USER_UPDATED' && session) {
        const profile = await get()._fetchProfile(session.user.id)
        set({ session, user: profile })
        return
      }
      if (session) {
        const profile = await get()._fetchProfile(session.user.id)
        set({ session, user: profile, loading: false })
      } else {
        set({ session: null, user: null, loading: false })
      }
    })

    // getSession() processes the URL hash (recovery tokens etc.)
    const { data: { session } } = await supabase.auth.getSession()

    // Only set initial state if PASSWORD_RECOVERY hasn't already handled it
    if (!get().passwordRecovery) {
      if (session) {
        const profile = await get()._fetchProfile(session.user.id)
        set({ session, user: profile, loading: false })
      } else {
        set({ loading: false })
      }
    }
  },

  /** Fetch the profile row for a given Supabase user id */
  _fetchProfile: async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    // Sync browser timezone to profile (silent, best-effort)
    if (data) {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (browserTz && data.timezone !== browserTz) {
        supabase.from('profiles').update({ timezone: browserTz }).eq('id', userId).then(() => {})
        data.timezone = browserTz
      }

      // Check for pending shared tasks
      const { data: shares } = await supabase
        .from('shared_tasks')
        .select('*')
        .eq('recipient_email', data.email)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())

      if (shares?.length) {
        // Update recipient_id if null (user registered after share was sent)
        const needsUpdate = shares.filter(s => !s.recipient_id)
        if (needsUpdate.length) {
          await supabase
            .from('shared_tasks')
            .update({ recipient_id: userId })
            .in('id', needsUpdate.map(s => s.id))
        }
        set({ pendingShares: shares })
      }
    }

    return data
  },

  /**
   * Sign up — Supabase creates the auth user; the DB trigger
   * (handle_new_user) automatically inserts a row in public.profiles.
   */
  register: async ({ name, familyName, email, password }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Redirect to the current origin after email confirmation
        // Works correctly in both localhost and production
        emailRedirectTo: window.location.origin + '/',
        data: {
          name,
          familyName,
          avatar: name.charAt(0).toUpperCase()
        }
      }
    })
    if (error) throw error
    return data
  },

  /** Sign in with email + password */
  login: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    if (error) throw error
    return data
  },

  /** Re-fetch profile from DB (e.g. after Stripe payment) without re-registering listeners */
  refreshProfile: async () => {
    const session = get().session
    if (!session) return
    const profile = await get()._fetchProfile(session.user.id)
    set({ user: profile })
  },

  /** Sign out and clear local state */
  logout: async () => {
    // Clear local state immediately so the UI responds
    set({ user: null, session: null })
    // Then try to sign out server-side (with timeout so it never blocks)
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise(r => setTimeout(r, 3000)),
      ])
    } catch {}
  },

  /** Check for pending shared tasks (called on login + app focus) */
  checkPendingShares: async () => {
    const user = get().user
    if (!user?.email) return
    const { data: shares } = await supabase
      .from('shared_tasks')
      .select('*')
      .eq('recipient_email', user.email)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
    set({ pendingShares: shares || [] })
  },

  /** Accept a shared task — import it and mark as accepted.
   *  `sharedOriginId` lega la copia al ledger: usato da addTask per setlare
   *  `tasks.shared_origin_id` (così il trigger DB notifica il sender quando
   *  questa copia viene completata/eliminata). */
  acceptShare: async (share) => {
    const userId = get().user?.id
    if (!userId) return
    await supabase.from('shared_tasks')
      .update({ status: 'accepted', accepted_at: new Date().toISOString() })
      .eq('id', share.id)
    set(s => ({ pendingShares: s.pendingShares.filter(sh => sh.id !== share.id) }))
    // Notifica il sender (fire-and-forget). La UI del sender si aggiornerà
    // comunque via Realtime sulla riga shared_tasks; questa è solo la push.
    notifyShareUpdate(share.id, 'accepted')
    return {
      title:           share.task_title,
      category:        share.task_category,
      dueDate:         share.task_due_date,
      dueTime:         share.task_due_time || null,
      priority:        share.task_priority,
      notes:           share.task_notes || '',
      reminder:        share.task_reminder ?? false,
      reminderMinutes: share.task_reminder_minutes ?? 15,
      durationMinutes: share.task_duration_minutes ?? null,
      sharedOriginId:  share.id,
      smartList:       share.task_smart_list ?? null,  // { title, items[] } | null
    }
  },

  /** Decline a shared task */
  declineShare: async (shareId) => {
    await supabase.from('shared_tasks')
      .update({ status: 'declined' })
      .eq('id', shareId)
    set(s => ({ pendingShares: s.pendingShares.filter(sh => sh.id !== shareId) }))
    notifyShareUpdate(shareId, 'declined')
  },

  /** Delete account and all associated data (ON DELETE CASCADE) */
  deleteAccount: async () => {
    // Cleanup file dell'utente su Storage PRIMA di cancellare l'account.
    // Dopo l'RPC l'utente non ha più auth.uid() e la RLS storage blocca tutto,
    // lasciando i file in `practice-attachments` orfani (la CASCADE su FK
    // tocca solo le righe DB, non gli object Storage).
    // Best-effort: se questo step fallisce, procediamo comunque — meglio
    // qualche file orfano che un account "mezzo cancellato".
    try {
      const userId = get().user?.id
      if (userId) {
        // Struttura bucket: practice-attachments/{user_id}/{practice_id}/{doc_id}.{ext}
        // Liste le sub-folder (una per pratica) e poi i file dentro ciascuna.
        const { data: practiceFolders } = await supabase.storage
          .from('practice-attachments')
          .list(userId, { limit: 1000 })
        const allPaths = []
        for (const folder of (practiceFolders || [])) {
          // folder.id è null per le "cartelle" virtuali di Supabase Storage
          if (folder.id !== null) continue
          const subPrefix = `${userId}/${folder.name}`
          const { data: files } = await supabase.storage
            .from('practice-attachments')
            .list(subPrefix, { limit: 1000 })
          for (const f of (files || [])) {
            allPaths.push(`${subPrefix}/${f.name}`)
          }
        }
        if (allPaths.length) {
          await supabase.storage.from('practice-attachments').remove(allPaths)
        }
      }
    } catch (e) {
      console.warn('[deleteAccount] storage cleanup failed (continuing):', e?.message)
    }

    const { error } = await supabase.rpc('delete_own_account')
    if (error) throw error
    await supabase.auth.signOut()
    set({ user: null, session: null })
  }
}))

export default useAuthStore
